import { forwardRef, useImperativeHandle, useMemo, useState } from "react";
import { Form, message, Modal } from "antd";
import { useRequest } from "ahooks";
import { EditForm, FormConfigType } from "../../../components/editForm";
import { GlobalPayServer } from "../../../server";

const server = GlobalPayServer.PayConfig;

const DEFAULT_OPERATION_DATA = {
    ChannelId: 1,
    CurrencyId: undefined,
    MinLimit: 0,
    MaxLimit: 0,
    ServiceFeeRate: 0,
    ServiceFeeDiscount: 0,
    ChannelFee: 0,
    ChannelFeeDiscount: 0,
    ExchangeRateLippage: 0,
    Sort: 0,
}

type OperateModeType = "create" | "update"

const toFormData = (record: any) => ({
    Id: record?.Id,
    ChannelId: 1,
    CurrencyId: record?.CurrencyId,
    MinLimit: record?.MinLimit || 0,
    MaxLimit: record?.MaxLimit || 0,
    ServiceFeeRate: record?.ServiceFeeRate || 0,
    ServiceFeeDiscount: record?.ServiceFeeDiscount || 0,
    ChannelFee: record?.ChannelFee || 0,
    ChannelFeeDiscount: record?.ChannelFeeDiscount || 0,
    ExchangeRateLippage: record?.ExchangeRateSlippage || 0,
    Sort: record?.Sort || 0,
})

export const Operation = forwardRef((props: any, ref: any) => {
    const { onSuccess, options } = props;
    const [loading, setLoading] = useState<boolean>(false);
    const [visible, setVisible] = useState<boolean>(false);
    const [formData, setFormData] = useState<any>({ ...DEFAULT_OPERATION_DATA });
    const [form] = Form.useForm();
    const [mode, setMode] = useState<OperateModeType>("create");

    const changeVisible = (val: any, mode?: OperateModeType) => {
        if (val) {
            const data = mode === "update" ? toFormData(val) : { ...DEFAULT_OPERATION_DATA };
            mode && setMode(mode);
            form?.resetFields?.();
            setFormData(data);
        } else {
            form?.resetFields?.();
        }
        setVisible(!!val);
    }

    useImperativeHandle(ref, () => ({ changeVisible }));

    const { run } = useRequest(mode === "create" ? server.create : server.update, {
        manual: true,
        onBefore: () => setLoading(true),
        onFinally: () => setLoading(false),
        onError: () => changeVisible(false),
        onSuccess: () => {
            message.success(mode === "create" ? "新增成功" : "更新成功");
            changeVisible(false);
            onSuccess?.();
        }
    });

    const formConfig: FormConfigType = useMemo(() => {
        const numberStyle = { width: "100%" };
        return [
            {
                title: "币种",
                field: "CurrencyId",
                type: "select",
                placeholder: "请选择币种",
                options: options?.currencyOptions || [],
                fieldNames: { label: 'name', value: 'id' },
                allowClear: true,
                required: true,
                style: { width: "100%" },
                disabled: mode === "update",
            },
            {
                title: "最小限额",
                field: "MinLimit",
                type: "inputNumber",
                placeholder: "请输入最小限额",
                style: numberStyle,
            },
            {
                title: "最大限额",
                field: "MaxLimit",
                type: "inputNumber",
                placeholder: "请输入最大限额",
                style: numberStyle,
            },
            {
                title: "服务费率",
                field: "ServiceFeeRate",
                type: "inputNumber",
                placeholder: "请输入服务费率",
                style: numberStyle,
            },
            {
                title: "手续费优惠",
                field: "ServiceFeeDiscount",
                type: "inputNumber",
                placeholder: "请输入手续费优惠金额",
                style: numberStyle,
            },
            {
                title: "通道费(USD)",
                field: "ChannelFee",
                type: "inputNumber",
                placeholder: "请输入通道费",
                style: numberStyle,
            },
            {
                title: "通道费优惠",
                field: "ChannelFeeDiscount",
                type: "inputNumber",
                placeholder: "请输入通道费优惠",
                style: numberStyle,
            },
            {
                title: "汇率滑点",
                field: "ExchangeRateLippage",
                type: "inputNumber",
                placeholder: "请输入汇率滑点",
                style: numberStyle,
            },
            {
                title: "排序",
                field: "Sort",
                type: "inputNumber",
                placeholder: "请输入排序",
                style: numberStyle,
            },
        ];
    }, [mode, options]);

    return (
        <Modal
            title={mode === "create" ? "新增消费支付配置" : "编辑消费支付配置"}
            width={620}
            closeIcon={null}
            maskClosable={false}
            open={visible}
            onCancel={() => changeVisible(false)}
            okButtonProps={{ loading }}
            onOk={async () => {
                await form.validateFields();
                run({
                    ...formData,
                    ChannelId: 1,
                    Page: 1,
                    PageSize: 10,
                });
            }}
        >
            <EditForm
                componentId="pay-config-operation"
                className="mt20"
                form={form}
                formAttrsProps={{
                    labelCol: { span: 8 },
                    wrapperCol: { span: 16 },
                }}
                formData={formData}
                onDataChange={setFormData}
                formConfig={formConfig}
            />
        </Modal>
    );
});
